import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { ASSETS, NAV_LINKS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
                scrolled
                    ? "bg-[#080B12]/90 backdrop-blur-xl border-[#D9A441]/25 shadow-[0_8px_40px_rgba(0,0,0,0.5)]"
                    : "bg-[#080B12]/40 backdrop-blur-md border-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("hero")}
                    className="flex items-center gap-3"
                    aria-label="BrothaTruth — back to top"
                >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold-gradient p-2 shadow-[0_0_20px_rgba(217,164,65,0.45)]">
                        <img
                            src={ASSETS.monogram}
                            alt="BrothaTruth BT monogram"
                            className="h-full w-full object-contain"
                        />
                    </span>
                </button>

                <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.id}
                            data-testid={link.testId}
                            onClick={() => go(link.id)}
                            className="group relative text-[13px] font-medium uppercase tracking-[0.14em] text-[#F8F5EE]/80 transition-colors duration-300 hover:text-[#F4C46B]"
                        >
                            {link.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-[#F4C46B] to-[#D9A441] transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        data-testid="nav-cta-get-started"
                        onClick={() => go("community")}
                        className="btn-shine hidden items-center gap-2 rounded-full bg-gold-gradient px-6 py-2.5 text-[13px] font-bold uppercase tracking-[0.12em] text-[#080B12] shadow-[0_0_24px_rgba(217,164,65,0.35)] transition-transform duration-300 hover:scale-[1.04] sm:flex"
                    >
                        Get Started
                        <ArrowRight className="h-4 w-4" />
                    </button>
                    <button
                        data-testid="nav-mobile-menu-button"
                        onClick={() => setOpen((v) => !v)}
                        className="rounded-full border border-[#D9A441]/40 p-2.5 text-[#F4C46B] lg:hidden"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-drawer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-[#D9A441]/20 bg-[#080B12]/95 backdrop-blur-xl lg:hidden"
                        aria-label="Mobile"
                    >
                        <div className="flex flex-col gap-1 px-6 py-5">
                            {NAV_LINKS.map((link, i) => (
                                <motion.button
                                    key={link.id}
                                    data-testid={`nav-mobile-${link.id}`}
                                    initial={{ opacity: 0, x: -14 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                    onClick={() => go(link.id)}
                                    className="rounded-lg px-3 py-3 text-left font-display text-sm uppercase tracking-[0.2em] text-[#F8F5EE]/85 transition-colors hover:bg-[#D9A441]/10 hover:text-[#F4C46B]"
                                >
                                    {link.label}
                                </motion.button>
                            ))}
                            <button
                                data-testid="nav-mobile-cta-get-started"
                                onClick={() => go("community")}
                                className="btn-shine mt-3 flex items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#080B12]"
                            >
                                Get Started
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
